import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getSubscription as getSubscriptionApi,
  createCheckoutSession as createCheckoutSessionApi,
  createPortalSession as createPortalSessionApi,
  changePlan as changePlanApi,
  cancelSubscription as cancelSubscriptionApi,
  resumeSubscription as resumeSubscriptionApi,
  getInvoices as getInvoicesApi,
} from "../../services/Billing";

export const fetchSubscription = createAsyncThunk(
  "billing/fetchSubscription",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSubscriptionApi();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const createCheckout = createAsyncThunk(
  "billing/createCheckout",
  async ({ planId, billingInterval }, { rejectWithValue }) => {
    try {
      const response = await createCheckoutSessionApi(planId, billingInterval);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const openPortal = createAsyncThunk(
  "billing/openPortal",
  async (_, { rejectWithValue }) => {
    try {
      const response = await createPortalSessionApi();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const changeBillingPlan = createAsyncThunk(
  "billing/changePlan",
  async ({ planId, billingInterval }, { rejectWithValue }) => {
    try {
      const response = await changePlanApi(planId, billingInterval);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const cancelBilling = createAsyncThunk(
  "billing/cancel",
  async (_, { rejectWithValue }) => {
    try {
      const response = await cancelSubscriptionApi();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const resumeBilling = createAsyncThunk(
  "billing/resume",
  async (_, { rejectWithValue }) => {
    try {
      const response = await resumeSubscriptionApi();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchInvoices = createAsyncThunk(
  "billing/fetchInvoices",
  async (limit, { rejectWithValue }) => {
    try {
      const response = await getInvoicesApi(limit);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

const billingSlice = createSlice({
  name: "billing",
  initialState: {
    subscription: null,
    plan: null,
    invoices: [],
    loading: false,
    actionLoading: false,
    error: null,
  },
  reducers: {
    clearBillingError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch subscription
      .addCase(fetchSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscription = action.payload.data?.subscription || null;
        state.plan = action.payload.data?.plan || null;
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.error.message;
      })
      // Create checkout
      .addCase(createCheckout.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createCheckout.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(createCheckout.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.error || action.error.message;
      })
      // Portal
      .addCase(openPortal.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(openPortal.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(openPortal.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.error || action.error.message;
      })
      // Change plan
      .addCase(changeBillingPlan.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(changeBillingPlan.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(changeBillingPlan.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.error || action.error.message;
      })
      // Cancel
      .addCase(cancelBilling.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(cancelBilling.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (state.subscription) {
          state.subscription.cancelAtPeriodEnd = true;
        }
      })
      .addCase(cancelBilling.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.error || action.error.message;
      })
      // Resume
      .addCase(resumeBilling.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(resumeBilling.fulfilled, (state) => {
        state.actionLoading = false;
        if (state.subscription) {
          state.subscription.cancelAtPeriodEnd = false;
          state.subscription.canceledAt = null;
        }
      })
      .addCase(resumeBilling.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.error || action.error.message;
      })
      // Invoices
      .addCase(fetchInvoices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload.data || [];
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.error.message;
      });
  },
});

export const { clearBillingError } = billingSlice.actions;
export default billingSlice.reducer;
